/** Minimal IndexedDB key-value store. Each mutate() runs in one transaction, so tabs never overwrite each other's changes. */

const DB_NAME = 'noorview-sandbox';
const STORE = 'kv';
let dbp: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  dbp ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => { db.close(); dbp = null; };
      resolve(db);
    };
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('The sandbox database is open in an older tab. Close other NoorView tabs and try again.'));
  });
  return dbp;
}

const done = (tx: IDBTransaction) => new Promise<void>((res, rej) => { tx.oncomplete = () => res(); tx.onerror = () => rej(tx.error); tx.onabort = () => rej(tx.error ?? new Error('Transaction aborted')); });

export async function idbGet<T>(key: string): Promise<T | undefined> {
  const db = await open();
  const tx = db.transaction(STORE, 'readonly');
  const req = tx.objectStore(STORE).get(key);
  await done(tx);
  return req.result as T | undefined;
}

/** Reads `key`, applies `fn` and writes the result back, all inside one read-write transaction. */
export async function idbMutate<T, R = void>(key: string, fn: (current: T | undefined) => { value: T; result: R }): Promise<R> {
  const db = await open();
  const tx = db.transaction(STORE, 'readwrite');
  const store = tx.objectStore(STORE);
  let out!: R;
  let failure: unknown = null;
  const req = store.get(key);
  req.onsuccess = () => {
    try {
      const { value, result } = fn(req.result as T | undefined);
      out = result;
      store.put(value, key);
    } catch (e) { failure = e; tx.abort(); }
  };
  try { await done(tx); } catch (e) { throw failure ?? e; }
  if (failure) throw failure;
  return out;
}

export async function idbClear(): Promise<void> {
  const db = await open();
  const tx = db.transaction(STORE, 'readwrite');
  tx.objectStore(STORE).clear();
  await done(tx);
}
